import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Save, X, Plus, Trash2, ArrowLeft, Edit, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useProtocolRisks } from '../../hooks/useRisks';
import { riskService } from '../../services/riskService';
import { useUserGroups } from '../../hooks/useUserGroups';
import { useActiveRoles } from '../../hooks/useRoles';
import { useAssignmentTypes, useShiftTypes } from '../../hooks/useTaskNomenclatures';
import { useVerificationMethods } from '../../hooks/useNomenclatures';
import { userService } from '../../services/firestoreService';
import ActionEditor from './ActionEditor';
import RiskSelectorModal from '../risks/RiskSelectorModal';
import Modal from '../ui/Modal';
import { useNotifications } from '../NotificationSystem';
import './ProtocolStepEditor.css';

// Sortable Action Item Component (t from parent)
const SortableActionItem = ({ action, onEdit, onDelete, t }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: action.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="action-item"
    >
      <div className="action-header">
        <div 
          className="action-info sortable-handle"
          {...attributes}
          {...listeners}
        >
          <span className="action-number">{action.order || action.actionNumber}</span>
          <span className="action-name">{action.name || (t ? t('protocols:stepEditor.actionDefaultName', { number: action.order || action.actionNumber }) : `Action ${action.order || action.actionNumber}`)}</span>
          <span className="action-type">{action.type}</span>
          <span className="action-duration">{action.estimatedDuration}min</span>
          {action.description && (
            <span className="action-description-inline">{action.description}</span>
          )}
        </div>
        <div className="action-actions">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEdit(action);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="btn btn-edit"
            title={t ? t('protocols:stepEditor.editAction') : 'Edit Action'}
          >
            <Edit size={14} />
            {t ? t('protocols:edit') : 'Edit'}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete(action.id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="btn btn-delete"
            title={t ? t('protocols:stepEditor.removeAction') : 'Remove Action'}
          >
            <Trash2 size={14} />
            {t ? t('protocols:commonRemove') : 'Remove'}
          </button>
        </div>
      </div>
      <div className="action-details">
        <span className="action-equipment">
          {t ? t('protocols:stepEditor.equipmentCount', { count: action.equipment?.length || 0 }) : `${action.equipment?.length || 0} equipment`}
        </span>
        <span className="action-supplies">
          {t ? t('protocols:stepEditor.suppliesCount', { count: action.supplies?.length || 0 }) : `${action.supplies?.length || 0} supplies`}
        </span>
      </div>
    </div>
  );
};

const ProtocolStepEditor = ({ step, onSave, onCancel, roomTypes, triggerEvents, protocolId, protocolName }) => {
  const { t } = useTranslation('protocols');
  const { notify } = useNotifications();
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'action'|'risk', name?, id?, scenario? }
  const { risks: protocolRisks, loading: risksLoading, refresh: refreshRisks } = useProtocolRisks(protocolId);
  
  // Personnel assignment hooks
  const { groups, loading: groupsLoading } = useUserGroups({ isActive: true });
  const { roles } = useActiveRoles();
  const { items: assignmentTypes } = useAssignmentTypes();
  const { items: shiftTypes } = useShiftTypes();
  const { items: verificationMethods } = useVerificationMethods();
  const [users, setUsers] = useState([]);
  
  // Load users for user-based assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await userService.getAll();
        setUsers(usersData || []);
      } catch (error) {
        console.error('Error loading users:', error);
        setUsers([]);
      }
    };
    loadUsers();
  }, []);

  // Auto-expand risks section when risks are present
  useEffect(() => {
    if (protocolRisks && protocolRisks.filter(r => r.level === 'step' && r.stepId === step.id).length > 0) {
      setRisksExpanded(true);
    }
  }, [protocolRisks, step.id]);
  
  const [formData, setFormData] = useState({
    ...step,
    name: step.name || '',
    description: step.description || '',
    order: step.order || 1,
    estimatedDuration: step.estimatedDuration || 5,
    isRequired: step.isRequired !== undefined ? step.isRequired : true,
    actions: step.actions || [],
    prerequisites: step.prerequisites || [],
    verificationMethods: step.verificationMethods || [],
    // NEW: Task Template Assignment
    generateTask: step.generateTask || false,
    taskTemplateId: step.taskTemplateId || null,
    taskTemplateName: step.taskTemplateName || null,
    taskPriority: step.taskPriority || 'medium',
    taskUrgency: step.taskUrgency || 'routine',
    // NEW: Personnel Assignment (Step Level)
    assignmentRules: step.assignmentRules || {
      assignmentType: null,
      assignedToGroup: null,
      assignedToGroupName: null,
      requiredRoles: [],
      assignedToUser: null,
      assignedToUserName: null,
      shiftType: null,
      preferredShift: null,
      workloadThreshold: 5,
      assignmentNotes: ''
    }
  });

  const [activeAction, setActiveAction] = useState(null);
  const [showActionEditor, setShowActionEditor] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Risk management state
  const [showRiskSelector, setShowRiskSelector] = useState(false);
  const [risksExpanded, setRisksExpanded] = useState(false);
  const [actionsExpanded, setActionsExpanded] = useState(true);

  // Enhanced validation
  const validateForm = () => {
    const newErrors = {};
    
    // Step name validation
    if (!formData.name.trim()) {
      newErrors.name = t('stepEditor.stepNameRequiredVal');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('stepEditor.stepNameMinLength');
    } else if (formData.name.trim().length > 200) {
      newErrors.name = t('stepEditor.stepNameMaxLength');
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('stepEditor.stepDescriptionRequiredVal');
    } else if (formData.description.trim().length < 10) {
      newErrors.description = t('stepEditor.stepDescriptionMinLengthVal');
    } else if (formData.description.trim().length > 2000) {
      newErrors.description = t('stepEditor.stepDescriptionMaxLength');
    }
    
    if (!formData.order || formData.order < 1) {
      newErrors.order = t('stepEditor.orderMinVal');
    } else if (formData.order > 999) {
      newErrors.order = t('stepEditor.orderMaxVal');
    }
    
    if (!formData.estimatedDuration || formData.estimatedDuration <= 0) {
      newErrors.estimatedDuration = t('stepEditor.durationMinVal');
    } else if (formData.estimatedDuration > 1440) {
      newErrors.estimatedDuration = t('stepEditor.durationMaxVal');
    }
    
    if (formData.actions && formData.actions.length > 0) {
      const actionOrders = new Set();
      const actionNames = new Set();
      formData.actions.forEach((action, index) => {
        const n = index + 1;
        if (!action.name.trim()) {
          newErrors[`action_${index}_name`] = t('protocols:actionNNameRequired', { step: n, action: n });
        } else if (action.name.trim().length < 3) {
          newErrors[`action_${index}_name`] = t('protocols:actionNNameMinLength', { step: n, action: n });
        } else if (actionNames.has(action.name.trim().toLowerCase())) {
          newErrors[`action_${index}_name`] = t('protocols:actionNNameUnique', { step: n, action: n });
        } else {
          actionNames.add(action.name.trim().toLowerCase());
        }
        if (!action.description.trim()) {
          newErrors[`action_${index}_description`] = t('protocols:actionDescriptionRequired', { step: n, action: n });
        }
        if (action.order && actionOrders.has(action.order)) {
          newErrors[`action_${index}_order`] = t('protocols:actionNOrderUnique', { step: n, action: n });
        } else if (action.order) {
          actionOrders.add(action.order);
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Assignment rules handlers
  const handleAssignmentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      assignmentRules: {
        ...prev.assignmentRules,
        [field]: value
      }
    }));
  };

  // Action management
  const handleAddAction = () => {
    const newAction = {
      id: Date.now().toString(),
      actionNumber: formData.actions.length + 1,
      order: formData.actions.length + 1,
      name: '',
      description: '',
      type: 'manual', // manual, automated, verification
      estimatedDuration: 2,
      isRequired: true,
      equipment: [],
      supplies: [],
      verificationMethod: '',
      notes: ''
    };
    
    // Don't add to formData yet - only when user clicks Save
    setActiveAction(newAction);
    setShowActionEditor(true);
    
    // Try multiple scroll methods
    setTimeout(() => {
      // Method 1: Standard window scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Method 2: Try to find and scroll the action editor header
      const actionEditorHeader = document.querySelector('.action-editor-header');
      if (actionEditorHeader) {
        actionEditorHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      
      // Method 3: Try to find the action editor container
      const actionEditor = document.querySelector('.action-editor');
      if (actionEditor) {
        actionEditor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    
    // Additional attempts
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 600);
  };

  const handleEditAction = (action) => {
    setActiveAction(action);
    setShowActionEditor(true);
    
    // Try multiple scroll methods
    setTimeout(() => {
      // Method 1: Standard window scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Method 2: Try to find and scroll the action editor header
      const actionEditorHeader = document.querySelector('.action-editor-header');
      if (actionEditorHeader) {
        actionEditorHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      
      // Method 3: Try to find the action editor container
      const actionEditor = document.querySelector('.action-editor');
      if (actionEditor) {
        actionEditor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    
    // Additional attempts
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 600);
  };

  const handleDeleteAction = (actionId) => {
    const actionToDelete = formData.actions.find(action => action.id === actionId);
    const actionName = actionToDelete ? actionToDelete.name || t('stepEditor.actionDefaultName', { number: actionToDelete.actionNumber || '' }) : '';
    setConfirmModal({ type: 'action', id: actionId, name: actionName });
  };

  const handleConfirmDeleteAction = () => {
    if (!confirmModal || confirmModal.type !== 'action') return;
    const actionId = confirmModal.id;
    setFormData(prev => ({
      ...prev,
      actions: prev.actions
        .filter(action => action.id !== actionId)
        .map((action, index) => ({
          ...action,
          actionNumber: index + 1
        }))
    }));
    setConfirmModal(null);
  };

  const handleActionSave = (updatedAction) => {
    setFormData(prev => {
      const existingActionIndex = prev.actions.findIndex(action => action.id === updatedAction.id);
      
      if (existingActionIndex >= 0) {
        // Update existing action
        const updatedActions = [...prev.actions];
        updatedActions[existingActionIndex] = updatedAction;
        return {
          ...prev,
          actions: updatedActions
        };
      } else {
        // Add new action
        return {
          ...prev,
          actions: [...prev.actions, updatedAction]
        };
      }
    });
    
    setShowActionEditor(false);
    setActiveAction(null);
    console.log('✅ Action saved successfully');
  };

  // Drag and drop handlers for actions
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before activating drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [activeActionId, setActiveActionId] = useState(null);

  const handleActionDragStart = (event) => {
    setActiveActionId(event.active.id);
  };

  const handleActionDragEnd = (event) => {
    const { active, over } = event;
    setActiveActionId(null);

    if (over && active.id !== over.id) {
      setFormData(prev => {
        const oldIndex = prev.actions.findIndex(action => action.id === active.id);
        const newIndex = prev.actions.findIndex(action => action.id === over.id);
        
        const newActions = arrayMove(prev.actions, oldIndex, newIndex);
        
        // Update order numbers
        const reorderedActions = newActions.map((action, index) => ({
          ...action,
          order: index + 1,
          actionNumber: index + 1
        }));
        
        return {
          ...prev,
          actions: reorderedActions
        };
      });
    }
  };

  // Risk management handlers
  const handleAddStepRisk = () => {
    setShowRiskSelector(true);
  };

  const handleRiskSelected = (association) => {
    setShowRiskSelector(false);
    setRisksExpanded(true); // Auto-expand when risk is added
    refreshRisks();
    console.log('✅ Risk associated to step successfully');
  };

  const handleRemoveRisk = (riskId, riskScenario) => {
    setConfirmModal({ type: 'risk', id: riskId, scenario: riskScenario });
  };

  const handleConfirmRemoveRisk = async () => {
    if (!confirmModal || confirmModal.type !== 'risk') return;
    try {
      await riskService.removeFromProtocol(confirmModal.id);
      refreshRisks();
      setConfirmModal(null);
    } catch (error) {
      console.error('Error removing risk:', error);
      notify.error(t('stepEditor.failedToRemoveRisk'));
    }
  };

  // Save step
  const handleSave = () => {
    if (!validateForm()) {
      console.log('❌ Step validation failed');
      return;
    }
    console.log('✅ Step validation passed, saving...');
    onSave(formData);
  };

  if (showActionEditor && activeAction) {
    return (
      <ActionEditor
        action={activeAction}
        onSave={handleActionSave}
        onCancel={() => {
          setShowActionEditor(false);
          setActiveAction(null);
        }}
        roomTypes={roomTypes}
        triggerEvents={triggerEvents}
        protocolId={protocolId}
        protocolName={protocolName}
        stepId={step.id}
        stepName={formData.name}
      />
    );
  }

  return (
    <div className="step-editor" style={{ width: '100%', maxWidth: 'none', margin: 0 }}>
      <div className="step-editor-header">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          <ArrowLeft size={16} />
          {t('stepEditor.backToProtocol')}
        </button>
        
        <h3>{t('stepEditor.editStepNumber', { number: step.stepNumber })}</h3>
        
        <div className="step-editor-actions">
          <button 
            type="button" 
            onClick={onCancel}
            className="btn btn-secondary"
          >
            <X size={16} />
            {t('cancel')}
          </button>
          <button 
            type="button" 
            onClick={handleSave}
            className="btn btn-primary"
          >
            <Save size={16} />
            {t('stepEditor.saveStep')}
          </button>
        </div>
      </div>

      <div className="step-editor-content">
        <div className="form-section">
          <h4>{t('stepEditor.stepInformation')}</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="stepName">{t('stepEditor.stepNameRequired')}</label>
              <input
                type="text"
                id="stepName"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={errors.name ? 'error' : ''}
                placeholder={t('stepEditor.enterStepName')}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="stepOrder">{t('stepEditor.orderRequired')}</label>
              <input
                type="number"
                id="stepOrder"
                value={formData.order}
                onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
                className={errors.order ? 'error' : ''}
                min="1"
                placeholder="1"
              />
              {errors.order && <span className="error-text">{errors.order}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="estimatedDuration">{t('stepEditor.durationMinutesRequired')}</label>
              <input
                type="number"
                id="estimatedDuration"
                value={formData.estimatedDuration}
                onChange={(e) => handleInputChange('estimatedDuration', parseInt(e.target.value) || 0)}
                className={errors.estimatedDuration ? 'error' : ''}
                min="1"
                placeholder="5"
              />
              {errors.estimatedDuration && <span className="error-text">{errors.estimatedDuration}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="isRequired">{t('stepEditor.required')}</label>
              <select
                id="isRequired"
                value={formData.isRequired ? 'true' : 'false'}
                onChange={(e) => handleInputChange('isRequired', e.target.value === 'true')}
              >
                <option value="true">{t('stepEditor.required')}</option>
                <option value="false">{t('stepEditor.optional')}</option>
              </select>
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="stepDescription">{t('stepEditor.descriptionRequired')}</label>
            <textarea
              id="stepDescription"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className={errors.description ? 'error' : ''}
              rows={3}
              placeholder={t('stepEditor.stepDescriptionPlaceholder')}
            />
            {errors.description && <span className="error-text">{errors.description}</span>}
          </div>
        </div>

        <div className="form-section">
          <h4>{t('stepEditor.taskGenerationOptional')}</h4>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.generateTask}
                onChange={(e) => handleInputChange('generateTask', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              {t('stepEditor.generateTaskFromStep')}
            </label>
            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
              {t('stepEditor.generateTaskFromStepHint')}
            </small>
          </div>

          {formData.generateTask && (
            <>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="taskPriority">{t('stepEditor.taskPriority')}</label>
                  <select
                    id="taskPriority"
                    value={formData.taskPriority}
                    onChange={(e) => handleInputChange('taskPriority', e.target.value)}
                  >
                    <option value="low">{t('stepEditor.low')}</option>
                    <option value="medium">{t('stepEditor.medium')}</option>
                    <option value="high">{t('stepEditor.high')}</option>
                    <option value="critical">{t('stepEditor.critical')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="taskUrgency">{t('stepEditor.taskUrgency')}</label>
                  <select
                    id="taskUrgency"
                    value={formData.taskUrgency}
                    onChange={(e) => handleInputChange('taskUrgency', e.target.value)}
                  >
                    <option value="routine">{t('stepEditor.routine')}</option>
                    <option value="urgent">{t('stepEditor.urgent')}</option>
                    <option value="emergency">{t('stepEditor.emergency')}</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="form-section">
          <h4>{t('stepEditor.personnelAssignmentStep')}</h4>
          <p className="section-description" style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
            {t('stepEditor.personnelAssignmentStepHint')}
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('stepEditor.assignmentType')}</label>
              <select
                value={formData.assignmentRules.assignmentType || ''}
                onChange={(e) => {
                  const assignmentType = e.target.value || null;
                  handleAssignmentChange('assignmentType', assignmentType);
                  if (assignmentType !== formData.assignmentRules.assignmentType) {
                    handleAssignmentChange('assignedToGroup', null);
                    handleAssignmentChange('assignedToGroupName', null);
                    handleAssignmentChange('requiredRoles', []);
                    handleAssignmentChange('assignedToUser', null);
                    handleAssignmentChange('assignedToUserName', null);
                    handleAssignmentChange('shiftType', null);
                  }
                }}
              >
                <option value="">{t('stepEditor.useProtocolAssignment')}</option>
                {assignmentTypes && assignmentTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
              <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                {t('stepEditor.assignmentStepHint')}
              </small>
            </div>

            {(() => {
              if (!formData.assignmentRules.assignmentType) return null;
              const selectedType = assignmentTypes?.find(ty => ty.id === formData.assignmentRules.assignmentType);
              const typeName = selectedType?.name?.toLowerCase() || '';
              if (typeName.includes('group')) {
                return (
                  <div className="form-group" key="group-assignment">
                    <label>{t('stepEditor.assignedToGroup')}</label>
                    <select
                      value={formData.assignmentRules.assignedToGroup || ''}
                      onChange={(e) => {
                        const groupId = e.target.value || null;
                        const group = groups.find(g => g.id === groupId);
                        handleAssignmentChange('assignedToGroup', groupId);
                        handleAssignmentChange('assignedToGroupName', group?.name || null);
                      }}
                      required
                    >
                      <option value="">{t('stepEditor.selectGroup')}</option>
                      {groupsLoading ? (
                        <option disabled>{t('stepEditor.loadingGroups')}</option>
                      ) : groups && groups.length > 0 ? (
                        groups.map(group => (
                          <option key={group.id} value={group.id}>{group.name || group.id}</option>
                        ))
                      ) : (
                        <option disabled>{t('stepEditor.noGroupsAvailable')}</option>
                      )}
                    </select>
                  </div>
                );
              }
              if (typeName.includes('role')) {
                return (
                  <div className="form-group" key="role-assignment">
                    <label>{t('stepEditor.requiredRoles')}</label>
                    <select
                      multiple
                      value={formData.assignmentRules.requiredRoles || []}
                      onChange={(e) => {
                        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                        handleAssignmentChange('requiredRoles', selectedOptions);
                      }}
                      size={Math.min(roles?.length || 3, 6)}
                      style={{ height: 'auto', minHeight: '100px' }}
                      required
                    >
                      {roles && roles.length > 0 ? (
                        roles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name || role.role_id}
                          </option>
                        ))
                      ) : (
                        <option disabled>{t('stepEditor.noRolesAvailable')}</option>
                      )}
                    </select>
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                      {t('stepEditor.holdCtrlToSelectRoles')}
                    </small>
                  </div>
                );
              }
              if (typeName.includes('shift')) {
                return (
                  <div className="form-group" key="shift-assignment">
                    <label>{t('stepEditor.shiftType')}</label>
                    <select
                      value={formData.assignmentRules.shiftType || ''}
                      onChange={(e) => handleAssignmentChange('shiftType', e.target.value || null)}
                      required
                    >
                      <option value="">{t('stepEditor.selectShift')}</option>
                      {shiftTypes && shiftTypes.map(shift => (
                        <option key={shift.id} value={shift.id}>{shift.name}</option>
                      ))}
                    </select>
                  </div>
                );
              }
              if (typeName.includes('user') || typeName.includes('direct')) {
                return (
                  <div className="form-group" key="user-direct-assignment">
                    <label>{typeName.includes('direct') ? t('stepEditor.assignToUserOptional') : t('stepEditor.assignToUser')}</label>
                    <select
                      value={formData.assignmentRules.assignedToUser || ''}
                      onChange={(e) => {
                        const userId = e.target.value || null;
                        const user = users.find(u => u.id === userId);
                        handleAssignmentChange('assignedToUser', userId);
                        handleAssignmentChange('assignedToUserName', user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : null);
                      }}
                      required={!typeName.includes('direct')}
                    >
                      <option value="">{t('stepEditor.selectUser')}</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name && user.last_name 
                            ? `${user.first_name} ${user.last_name} (${user.email || user.id})`
                            : user.email || user.id}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }
              
              return null;
            })()}
          </div>
        </div>

        <div className="form-section">
          <h4>{t('stepEditor.prerequisites')}</h4>
          <div className="form-group">
            <label htmlFor="prerequisites">{t('stepEditor.prerequisiteSteps')}</label>
            <input
              type="text"
              id="prerequisites"
              value={formData.prerequisites.join(', ')}
              onChange={(e) => handleArrayChange('prerequisites', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
              placeholder={t('stepEditor.prerequisitePlaceholder')}
            />
            <small>{t('stepEditor.prerequisiteHint')}</small>
          </div>
        </div>

        <div className="form-section">
          <h4>{t('stepEditor.verificationMethods')}</h4>
          <div className="form-group">
            <label htmlFor="verificationMethod">{t('stepEditor.verificationMethod')}</label>
            <select
              id="verificationMethod"
              value={formData.verificationMethods && formData.verificationMethods.length > 0 ? formData.verificationMethods[0] : ''}
              onChange={(e) => handleArrayChange('verificationMethods', e.target.value ? [e.target.value] : [])}
            >
              <option value="">{t('stepEditor.selectVerificationMethod')}</option>
              {verificationMethods.map(method => (
                <option key={method.id} value={method.name || method.code}>
                  {method.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Associated Risks (Step Level) */}
        {protocolId && step.id && (
          <div className="form-section">
            <div 
              className="section-header-collapsible"
              onClick={() => setRisksExpanded(!risksExpanded)}
            >
              <h4>
                <AlertTriangle size={18} />
                {t('stepEditor.associatedRisksStep')}
                {protocolRisks && protocolRisks.filter(r => r.level === 'step' && r.stepId === step.id).length > 0 && (
                  <span className="risk-count-badge">
                    {protocolRisks.filter(r => r.level === 'step' && r.stepId === step.id).length}
                  </span>
                )}
              </h4>
              <div className="section-header-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddStepRisk();
                  }}
                  className="btn btn-success section-add-btn"
                >
                  <Plus size={16} />
                  {t('stepEditor.addRisk')}
                </button>
                {risksExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>

            {risksExpanded && (
              <div className="risks-section-content">
                {risksLoading ? (
                  <div className="loading-state-small">
                    <div className="spinner-small"></div>
                    <span>{t('stepEditor.loadingRisks')}</span>
                  </div>
                ) : protocolRisks && protocolRisks.filter(r => r.level === 'step' && r.stepId === step.id).length > 0 ? (
                  <div className="risks-list">
                    {protocolRisks
                      .filter(r => r.level === 'step' && r.stepId === step.id)
                      .map(risk => (
                        <div key={risk.id} className="risk-item-compact">
                          <div className="risk-item-info">
                            <strong>{risk.riskScenario}</strong>
                            <div className="risk-item-meta">
                              <span className="rpn-badge-small">RPN: {risk.rpn}</span>
                              <span className={`criticality-badge-small criticality-${risk.criticality?.toLowerCase()}`}>
                                {risk.criticality}
                              </span>
                              {risk.riskVersion && (
                                <span className="version-badge-small">v{risk.riskVersion}</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveRisk(risk.id, risk.riskScenario)}
                            className="btn btn-delete"
                            title={t('stepEditor.removeRiskAssociation')}
                          >
                            <Trash2 size={14} />
                            {t('protocols:commonRemove')}
                          </button>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="no-risks-message">
                    {t('stepEditor.noStepRisksYet')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="form-section">
          <div 
            className="section-header-collapsible"
            onClick={() => setActionsExpanded(!actionsExpanded)}
          >
            <h4>
              {t('protocols:actions')}
              {formData.actions && formData.actions.length > 0 && (
                <span className="risk-count-badge">
                  {formData.actions.length}
                </span>
              )}
            </h4>
            <div className="section-header-actions">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddAction();
                }}
                className="btn btn-success section-add-btn"
              >
                <Plus size={16} />
                {t('stepEditor.addAction')}
              </button>
              {actionsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>

          {actionsExpanded && (
            <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleActionDragStart}
            onDragEnd={handleActionDragEnd}
          >
            <SortableContext
              items={formData.actions.map(a => a.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="actions-list">
                {formData.actions
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((action) => (
                    <SortableActionItem
                      key={action.id}
                      action={action}
                      onEdit={handleEditAction}
                      onDelete={handleDeleteAction}
                      t={t}
                    />
                  ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeActionId ? (
                <div className="action-item dragging">
                  <div className="action-header">
                    <div className="action-info">
                      <span className="action-number">
                        {formData.actions.find(a => a.id === activeActionId)?.order || ''}
                      </span>
                      <span className="action-name">
                        {formData.actions.find(a => a.id === activeActionId)?.name || ''}
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
          )}
        </div>

        {confirmModal && (
          <Modal
            isOpen={!!confirmModal}
            onClose={() => setConfirmModal(null)}
            title={confirmModal.type === 'action' ? t('stepEditor.removeAction') : t('stepEditor.removeRiskAssociation')}
          >
            <p>
              {confirmModal.type === 'action'
                ? t('stepEditor.confirmRemoveAction', { name: confirmModal.name })
                : t('stepEditor.confirmRemoveRisk', { scenario: confirmModal.scenario })}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmModal(null)}>
                {t('cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  if (confirmModal.type === 'action') handleConfirmDeleteAction();
                  else handleConfirmRemoveRisk();
                }}
              >
                {t('protocols:commonRemove')}
              </button>
            </div>
          </Modal>
        )}

        {showRiskSelector && protocolId && step.id && (
          <RiskSelectorModal
            protocolId={protocolId}
            protocolName={protocolName}
            level="step"
            stepId={step.id}
            stepName={formData.name}
            actionId={null}
            actionName={null}
            onSelect={handleRiskSelected}
            onClose={() => setShowRiskSelector(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ProtocolStepEditor;
